import { useCallback, useEffect, useRef, useState, startTransition } from 'react';

import { useForm } from '@mantine/form';
import { diff } from 'deep-object-diff';
import { useTranslation } from 'react-i18next';

import { AppUtils } from 'utils';

interface UseAutosaveProps<T> {
  credit: T;
  updateService: (id: number, data: Partial<T>) => Promise<void>;
  getVisibleFieldsFn: (values: T) => string[];
  readOnly?: boolean;
  formValidation?: any;
  initialValuesTransform?: (credit: T) => T;
}

export const useFormAutosave = <T extends { id: number }>({
  credit,
  updateService,
  getVisibleFieldsFn,
  readOnly = false,
  formValidation = {},
  initialValuesTransform,
}: UseAutosaveProps<T>) => {
  const { t } = useTranslation();
  const updatingTimeout = useRef<NodeJS.Timeout>();

  const [visibleFields, setVisibleFields] = useState<string[]>(() => {
    const transformedValues = initialValuesTransform
      ? initialValuesTransform(credit)
      : (credit as T);

    return getVisibleFieldsFn(transformedValues);
  });
  const valuesToSend = useRef<Partial<T>>({});
  const isInternalUpdate = useRef<boolean>(false);
  const isInitialized = useRef<boolean>(false);
  const visibleFieldsRef = useRef<string[]>(visibleFields);
  visibleFieldsRef.current = visibleFields;

  const updateCredit = useCallback(
    async (item: Partial<T>) => {
      if (Object.keys(item).length === 0) {
        return;
      }
      clearTimeout(updatingTimeout.current);

      const clearedItem = { ...item };
      delete (clearedItem as any).additionalExpenses;

      valuesToSend.current = { ...valuesToSend.current, ...clearedItem };
      updatingTimeout.current = setTimeout(async () => {
        updateService(credit.id, valuesToSend.current);
        valuesToSend.current = {};
      }, 1000);
    },
    [credit.id, updateService],
  );

  const updateFields = useCallback(
    async (values: T, previous: T) => {
      if (readOnly || isInternalUpdate.current || !isInitialized.current || Object.keys(previous).length === 0) return;
      isInternalUpdate.current = true;

      const diffValues: Partial<T> = diff(previous, values) as Partial<T>;
      if (Object.keys(diffValues).length === 0) {
        isInternalUpdate.current = false;
        return;
      }

      if ((diffValues as any)['creditAmount'] || (diffValues as any)['paymentPeriod']) {
        AppUtils.populateMonthlyInstallment((values as any).creditAmount, (values as any).paymentPeriod, form);
        const updatedValues = form.getValues();
        (diffValues as any)['monthlyInstallmentFrom'] = (updatedValues as any).monthlyInstallmentFrom;
        (diffValues as any)['monthlyInstallmentTo'] = (updatedValues as any).monthlyInstallmentTo;
      }

      const prevVisibleFields = visibleFieldsRef.current;
      const newVisibleFields: string[] = getVisibleFieldsFn(values);

      // Array fields that need to send full value, not partial diff
      const arrayFields = ['children', 'employments', 'incomes', 'expenses', 'mortgageExpenses', 'housingExpenses'];

      if ((diffValues as any)['children']) (diffValues as any)['children'] = (values as any)['children'];
      if ((diffValues as any)['employments']) (diffValues as any)['employments'] = (values as any)['employments'];
      if ((diffValues as any)['incomes']) (diffValues as any)['incomes'] = (values as any)['incomes'];
      if ((diffValues as any)['expenses']) (diffValues as any)['expenses'] = (values as any)['expenses'];
      if ((diffValues as any)['mortgageExpenses']) (diffValues as any)['mortgageExpenses'] = (values as any)['mortgageExpenses'];
      if ((diffValues as any)['housingExpenses']) (diffValues as any)['housingExpenses'] = (values as any)['housingExpenses'];

      // Clear fields that became invisible
      prevVisibleFields.forEach((el) => {
        if (!newVisibleFields.includes(el) && (previous as any)[el] !== undefined) {
          // Array fields should be cleared with empty array, not null
          (diffValues as any)[el] = arrayFields.includes(el) ? [] : null;
        }
      });
      startTransition(() => {
        Object.entries(diffValues).forEach(([key, val]) => {
          form.setFieldValue(key, val, { forceUpdate: false });
        });
        setVisibleFields(newVisibleFields);
      });
      updateCredit(diffValues);
      isInternalUpdate.current = false;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [readOnly, getVisibleFieldsFn, updateCredit],
  );

  const form = useForm<T>({
    mode: 'uncontrolled',
    initialValues: {} as T,
    validateInputOnChange: false,
    validate: {
      houseNumber: (value: any) =>
        value && !/^[0-9]/.test(value.charAt(0)) ? t('user.address.houseNumber.startWith1') : null,
      previousHouseNumber: (value: any) =>
        value && !/^[0-9]/.test(value.charAt(0)) ? t('user.address.houseNumber.startWith1') : null,
      ...formValidation,
    },
    onValuesChange: (values, previous) => {
      updateFields(values, previous);
    },
  });

  useEffect(() => {
    const transformedValues = initialValuesTransform
      ? initialValuesTransform(credit)
      : (credit as T);
    const nextVisibleFields = getVisibleFieldsFn(transformedValues);

    isInitialized.current = false;
    isInternalUpdate.current = true;
    form.setValues(transformedValues);
    visibleFieldsRef.current = nextVisibleFields;
    setVisibleFields(nextVisibleFields);

    // Mark as initialized after a short delay to allow all initial renders to complete
    const initializationTimeout = setTimeout(() => {
      isInternalUpdate.current = false;
      isInitialized.current = true;
    }, 0);

    return () => clearTimeout(initializationTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credit]);

  return {
    form,
    visibleFields,
    updateCredit,
  };
};
